/**
 * @file 极简补间（Tween）工具 —— ADR-001「动画」落地。
 *
 * 不引第三方 tween 库：用 Pixi 的 Ticker 驱动一个自管队列，
 * 每帧写对象的 `x / y / alpha / scale`。只做线性与平方缓出，够用且可预测。
 */

import type { Container } from 'pixi.js';

/** 可被补间的对象（任意含这些数值属性的 Pixi 对象）。 */
export interface Tweenable {
  x: number;
  y: number;
  alpha: number;
  scaleX: number;
  scaleY: number;
}

export interface TweenSpec {
  readonly from: Partial<Tweenable>;
  readonly to: Partial<Tweenable>;
  readonly durationMs: number;
  readonly ease?: 'linear' | 'out' | 'in';
  readonly onComplete?: () => void;
}

interface ActiveTween {
  readonly target: Container;
  readonly from: Partial<Tweenable>;
  readonly to: Partial<Tweenable>;
  readonly durationMs: number;
  readonly ease: 'linear' | 'out' | 'in';
  readonly onComplete?: () => void;
  elapsed: number;
}

function easeFn(kind: 'linear' | 'out' | 'in', t: number): number {
  if (kind === 'out') return 1 - (1 - t) * (1 - t);
  if (kind === 'in') return t * t;
  return t;
}

/** 把 tween 的 from/to 里的字段应用到 target。 */
function applyField(target: Container, field: keyof Tweenable, value: number): void {
  switch (field) {
    case 'x':
      target.x = value;
      break;
    case 'y':
      target.y = value;
      break;
    case 'alpha':
      target.alpha = value;
      break;
    case 'scaleX':
      target.scale.x = value;
      break;
    case 'scaleY':
      target.scale.y = value;
      break;
  }
}

export class TweenRunner {
  private readonly tweens: ActiveTween[] = [];

  /** 每帧推进，由外层 Ticker 调用。 */
  update(deltaMs: number): void {
    if (this.tweens.length === 0) return;
    for (let i = this.tweens.length - 1; i >= 0; i--) {
      const tw = this.tweens[i];
      if (tw === undefined) continue;
      tw.elapsed += deltaMs;
      const t = Math.min(1, tw.elapsed / tw.durationMs);
      const k = easeFn(tw.ease, t);
      for (const field of Object.keys(tw.to) as (keyof Tweenable)[]) {
        const from = tw.from[field] ?? 0;
        const to = tw.to[field] ?? 0;
        applyField(tw.target, field, from + (to - from) * k);
      }
      if (t >= 1) {
        this.tweens.splice(i, 1);
        tw.onComplete?.();
      }
    }
  }

  /** 添加一条补间。同一 target 上已有补间会被覆盖（取消旧的）。 */
  add(target: Container, spec: TweenSpec): void {
    this.cancel(target);
    const from: Partial<Tweenable> = {};
    const to: Partial<Tweenable> = {};
    for (const field of Object.keys(spec.to) as (keyof Tweenable)[]) {
      const targetValue = spec.to[field];
      if (targetValue === undefined) continue;
      from[field] = spec.from?.[field] ?? readField(target, field);
      to[field] = targetValue;
    }
    this.tweens.push({
      target,
      from,
      to,
      durationMs: Math.max(1, spec.durationMs),
      ease: spec.ease ?? 'linear',
      ...(spec.onComplete !== undefined ? { onComplete: spec.onComplete } : {}),
      elapsed: 0,
    });
  }

  /** 取消某 target 上的所有补间。 */
  cancel(target: Container): void {
    for (let i = this.tweens.length - 1; i >= 0; i--) {
      if (this.tweens[i]?.target === target) this.tweens.splice(i, 1);
    }
  }

  /** 清空全部。 */
  clear(): void {
    this.tweens.length = 0;
  }

  get count(): number {
    return this.tweens.length;
  }
}

function readField(target: Container, field: keyof Tweenable): number {
  switch (field) {
    case 'x':
      return target.x;
    case 'y':
      return target.y;
    case 'alpha':
      return target.alpha;
    case 'scaleX':
      return target.scale.x;
    case 'scaleY':
      return target.scale.y;
  }
}
